import { Box, Button, InputBase, Theme, Typography, useMediaQuery } from "@mui/material";
import usePagination from "@mui/material/usePagination";
import { useEffect, useState } from "react";
import { ChevronRightGlyph } from "../components/SignRequestsIcons";
import inboxStyles from "./Inbox.styles";
import inboxTokens from "./Inbox.tokens";
import { ChevronLeftGlyph } from "./InboxIcons";

const styles = inboxStyles;
const { footer } = inboxTokens;

interface InboxPaginationProps {
  /** 1-based index of the page on screen. */
  page: number;
  totalPages: number;
  /** Inclusive 1-based bounds of the rows on screen, e.g. `[1, 10]`. */
  range: [number, number];
  total: number;
  label: string;
  onChange: (page: number) => void;
}

const arrowIconSx = {
  width: footer.arrowIconSize,
  height: footer.arrowIconSize,
};

/**
 * "Showing 1–10 of 344 requests" on the left, pager on the right.
 *
 * The page list comes from MUI's `usePagination` so the window collapses to a
 * constant seven slots (`1 … 18 19 20 … 35`) however many pages there are —
 * only the item chrome is ours. Past `jumpMinPages` a jump-to-page field is
 * added, since by then the window is hiding pages the user can't click to.
 *
 * A phone has room for neither: the reference keeps the count and the pager on
 * one line, so the jump field is hidden and the window drops to `1 … 5 … 20`.
 */
const InboxPagination = ({
  page,
  totalPages,
  range,
  total,
  label,
  onChange,
}: InboxPaginationProps) => {
  const [jumpValue, setJumpValue] = useState<string>("");
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { items } = usePagination({
    count: totalPages,
    page,
    boundaryCount: footer.boundaryCount,
    siblingCount: isPhone ? 0 : footer.siblingCount,
    onChange: (_event, value: number) => onChange(value),
  });

  // A jump leaves its own number behind; clear it once the page catches up so
  // the field never disagrees with the highlighted button.
  useEffect(() => setJumpValue(""), [page]);

  const commitJump = () => {
    if (!jumpValue) return;
    const target = Math.min(Math.max(Number(jumpValue), 1), totalPages);
    setJumpValue("");
    if (target !== page) onChange(target);
  };

  const renderItem = (
    item: ReturnType<typeof usePagination>["items"][number],
    index: number,
  ) => {
    const { type, page: itemPage, selected, disabled, onClick } = item;

    if (type === "start-ellipsis" || type === "end-ellipsis") {
      return (
        <Box key={`${type}-${index}`} component="span" sx={styles.pagerEllipsis}>
          …
        </Box>
      );
    }

    if (type === "previous" || type === "next") {
      const isPrevious = type === "previous";
      return (
        <Button
          key={type}
          disableRipple
          aria-label={isPrevious ? "Previous page" : "Next page"}
          disabled={disabled}
          onClick={onClick}
          sx={styles.pagerArrow}
        >
          {isPrevious ? (
            <ChevronLeftGlyph sx={arrowIconSx} />
          ) : (
            <ChevronRightGlyph sx={arrowIconSx} />
          )}
        </Button>
      );
    }

    return (
      <Button
        key={`page-${itemPage}`}
        disableRipple
        aria-label={`Page ${itemPage}`}
        aria-current={selected ? "page" : undefined}
        onClick={onClick}
        sx={styles.pagerPage(selected)}
      >
        {itemPage}
      </Button>
    );
  };

  return (
    <Box sx={styles.footer}>
      {/*
        The phone reference shortens the count to "1–5 of 11"; from `sm` up it
        reads "Showing 1–5 of 11 requests". Both halves are in the DOM and the
        breakpoint hides the ones it does not want.
      */}
      <Typography component="span" sx={styles.footerLabel}>
        <Box component="span" sx={styles.footerLabelPrefix}>
          Showing{" "}
        </Box>
        {`${range[0]}–${range[1]} of ${total}`}
        <Box component="span" sx={styles.footerLabelPrefix}>
          {` ${label}`}
        </Box>
      </Typography>
      <Box sx={styles.pager}>
        {!isPhone && totalPages >= footer.jumpMinPages && (
          <Box sx={styles.jumpGroup}>
            <Typography component="span" sx={styles.jumpLabel}>
              Go to
            </Typography>
            <Box sx={styles.jumpInput}>
              <InputBase
                inputProps={{
                  inputMode: "numeric",
                  maxLength: String(totalPages).length,
                  "aria-label": `Go to page, 1 to ${totalPages}`,
                }}
                value={jumpValue}
                placeholder={String(page)}
                onChange={(event) =>
                  setJumpValue(event.target.value.replace(/\D/g, ""))
                }
                onBlur={commitJump}
                onKeyDown={(event) => {
                  if (event.key === "Enter") commitJump();
                }}
              />
            </Box>
          </Box>
        )}
        {items.map(renderItem)}
      </Box>
    </Box>
  );
};

export default InboxPagination;
